import { useState, type DragEvent } from 'react';
import { Link } from 'react-router-dom';
import { Folder } from 'lucide-react';
import type { Board } from '@/lib/api/types';
import { hasAssetDrag, hasFileDrag, readAssetDragData, type AssetDragPayload } from '@/features/grid/dnd';
import { cx, plural } from '@/lib/format';

export function BoardCard({ board, to, onDropAssets, onDropFiles, onContextMenu, size = 'md' }: { board: Board; to: string; onDropAssets?: (b: Board, p: AssetDragPayload, copy: boolean) => void; onDropFiles?: (b: Board, dt: DataTransfer) => void; onContextMenu?: (e: React.MouseEvent, b: Board) => void; size?: 'sm' | 'md' }) {
  const [over, setOver] = useState(false);
  const thumb = board.cover?.thumbnails?.['512'] ?? board.cover?.thumbnails?.['256'] ?? null;
  const smart = board.kind === 'smart'; // release2/A: a live query takes no assets or files (409 board_is_smart)
  const onDragOver = (e: DragEvent) => {
    if (smart) return;
    const assets = hasAssetDrag(e);
    const files = hasFileDrag(e);
    if (!assets && !files) return;
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = assets ? (e.altKey ? 'copy' : 'move') : 'copy';
    if (!over) setOver(true);
  };
  return (
    <Link
      to={to}
      onContextMenu={onContextMenu ? (e) => onContextMenu(e, board) : undefined}
      onDragOver={onDragOver}
      onDragEnter={onDragOver}
      onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setOver(false); }} // L13: crossing into a child is not a leave
      onDrop={(e) => {
        setOver(false);
        if (smart) return;
        const p = readAssetDragData(e);
        if (p && onDropAssets) {
          e.preventDefault();
          e.stopPropagation();
          onDropAssets(board, p, e.altKey);
        } else if (hasFileDrag(e) && onDropFiles) {
          e.preventDefault();
          e.stopPropagation();
          onDropFiles(board, e.dataTransfer);
        }
      }}
      className={cx('group flex shrink-0 flex-col gap-1.5 rounded-lg border border-line bg-bg-elev p-2 transition-colors hover:border-line-strong', size === 'md' ? 'w-44' : 'w-36', over && 'border-accent bg-accent-soft ring-2 ring-accent')}
      data-board-id={board.id}
    >
      <div className={cx('relative w-full overflow-hidden rounded-md bg-bg-sunken', size === 'md' ? 'h-[120px]' : 'h-[90px]')} style={{ background: board.color ? `${board.color}22` : undefined }}>
        {thumb ? <img src={thumb} alt="" className="h-full w-full object-cover" loading="lazy" decoding="async" draggable={false} /> : <Folder size={28} className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-fg-subtle" style={{ color: board.color ?? undefined }} />}
      </div>
      <div className="min-w-0">
        <div className="truncate text-sm font-medium" title={board.name}>
          {board.name}
        </div>
        <div className="text-xs text-fg-subtle">
          {plural(board.asset_count_deep, 'asset', 'assets')}
          {board.child_board_count > 0 && ` · ${plural(board.child_board_count, 'board', 'boards')}`}
        </div>
      </div>
    </Link>
  );
}
